import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  // HashLocationStrategy,
  LocationStrategy,
  DatePipe,
  DecimalPipe,
  CurrencyPipe,
  PercentPipe,
  PathLocationStrategy,
} from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatNativeDateModule } from '@angular/material/core';

import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';

import { ToastrModule } from 'ngx-toastr';
import { UserIdleModule } from 'angular-user-idle';
import { SocketIoModule } from 'ngx-socket-io';
import {
  SocialLoginModule,
  SocialAuthServiceConfig,
  GoogleLoginProvider,
  FacebookLoginProvider,
} from '@abacritt/angularx-social-login';

import { environment } from 'environments/environment';

// Auth
import { AuthGuard } from './_guards/auth.guard';
import { PendingChangesGuard } from './_guards/pending-changes.guard';
import { AuthInterceptor } from './_interceptors/auth.interceptor';

// Services
import {
  AuthenticationService,
  ProfileService,
  CompanyService,
  SandboxService,
  AgencyService,
  AgencyConfigService,
  BudgetService,
  CompanyBudgetService,
  BureauService,
  BureauBudgetService,
  TopicService,
  EventService,
  DropdownService,
  CompanyDropdownSettingService,
  LanguageService,
  MeetingTypeService,
  QuestionService,
  EvaluationService,
  ChecklistService,
  WorkflowService,
  SpeakerTaskService,
  BureauTaskService,
  RequestFormService,
  RequestFormDefaultValueService,
  PreRequestFormService,
  BureauMeetingTypeService,
  ApprovalGroupService,
  SpeakerApprovalGroupService,
  StaffService,
  ClientService,
  SpeakerService,
  AreaService,
  AreaBudgetService,
  VendorService,
  RequestService,
  SettingService,
  FiscalYearService,
  DefaultBudgetService,
  ContactService,
  ContactNoteService,
  FileService,
  GlobalFileService,
  CallPlanService,
  CallPlanContactService,
  AttendeeService,
  HcpService,
  EventStatusService,
  EventStatusRequirementService,
  AreaTypeClientLevelService,
  MeetingTypeClientLevelService,
  EventStatusChangeService,
  EventChangeLogService,
  EventBudgetService,
  EventBudgetStatusChangeService,
  EventRescheduleService,
  EventVenueService,
  EventCatererService,
  EventChatMessageService,
  EventAvCompanyService,
  EventTrainingService,
  EventTrainingGroupService,
  CompanyProfileService,
  EquipmentService,
  SpeakerAgreementService,
  SpeakerValidationService,
  SpeakerValidationNoteService,
  SpeakerValidationFileService,
  EventSpeakerService,
  EventPaymentService,
  PaymentService,
  EventNearbyService,
  EventEstimatedTravelService,
  BudgetTransferService,
  BalanceTransactionService,
  EventCardService,
  DownloadFormService,
  DownloadFormClientLevelService,
  DownloadFormMeetingTypeService,
  EventDownloadFormService,
  ReportService,
  CustomReportService,
  EventCreditCardService,
  ActionService,
  NotificationService,
  TriggerService,
  UploadFormService,
  AvEquipmentService,
  VendorAvEquipmentService,
  CompanyAvEquipmentService,
  BureauAvEquipmentService,
  BureauSummaryService,
  EventEquipmentService,
  EventNoteService,
  EventContactService,
  EventNotificationService,
  EventEmailService,
  EventNotificationRuleService,
  EventFileService,
  EventChecklistService,
  EventEvaluationService,
  EventClientAnswerService,
  EventSpeakerAnswerService,
  AttendeeAnswerService,
  AttendeeAnswerAnonymousService,
  CallPlanCustomFieldService,
  ContactCustomFieldService,
  SpeakerCustomFieldService,
  AttendeeCustomFieldService,
  EventCustomFieldService,
  AttendeeRegistrationAnswerService,
  EventCollaborationService,
  ProductService,
  VendorRoomService,
  WorkflowTemplateService,
  DownloadFormGlobalService,
  ContactSubmissionService,
  KolQuestionnaireService,
  EsifFormService,
  SpeakerRateColService,
  SpeakerRateRowService,
  SpeakerRateDefaultService,
  SpeakerLevelService,
  EmailTemplateService,
  EmailTemplateAlignmentService,
  AgencyEmailTemplateService,
  StaticContentService,
  ContactFieldService,
  SpeakerEsifFormService,
  ForumService,
  StaffChecklistService,
  SupportContactService,
  AlertService,
  SessionSummaryService,
  UsageTrackService,
  UploadFormClientLevelService,
  ClientLevelService,
  NotificationCategoryService,
  BureauFiscalYearService,
  ChartService,
  EventSpeakerPaymentService,
  CoreEventFieldService,
  CoreEventFormService,
  CoreEventService,
  CoreEventAttendeeService,
  CoreEventTravelService,
  CoreEventActivityService,
  CoreEventNoteService,
  CoreInvitationService,
  CoreEventInvitationService,
  CoreEventChatMessageService,
  CoreEventChecklistService,
  CoreEventPaymentService,
  CoreEventBudgetService,
  CoreEventCreditCardService,
  CoreEventCatererService,
  CoreEventInquiryService,
  CoreEventCommunicationService,
  CoreEventAvService,
  CoreEventVenueService,
  CoreEventFileService,
  CoreEventEvaluationService,
  UserService,
  UserNotificationService,
  UserChecklistService,
  PresentationService,
  PresentationCustomService,
  PresentationQrcodeService,
  PresentationAnswerService,
  ContactAddressService,
  ContactLicenseService,
  ContactAssistantService,
  HonorariaRateColService,
  HonorariaRateRowService,
  HonorariaRateDefaultService,
  CompanyBureauAgencyService,
  CompanyBureauTravelAgentService,
  AreaTypeService,
  EventSpeakerTravelService,
  EventSpeakerStayService,
  EventViewRequestService,
  SpeakerAgreementFieldService,
  HourlyRateService,
  UserAlertService,
  EventPresentationService,
  EventClientSignService,
  EventSpeakerSignService,
  EventInquiryService,
  EventWebcastService,
  EventWebcastGroupService,
  SocketService,
  InvitationService,
  EventInvitationService,
  UnsubscribeService,
  UniverseService,
  VendorAgencyService,
  EventTokenService,
  AttendeeRegistrationFieldsService,
  S3Service,
  HcpValidationSourceService,
  WorkflowBudgetService,
  ClientEventViewService,
  ClientExpenseViewService,
  ClientFacultyViewService,
  ClientReportViewService,
  TrainingModuleService,
  EventSpeakerCarService,
  GlobalTriggerService,
  GlobalEmailTemplateService,
  BlastEmailTemplateService,
  GlobalNotificationService,
  CompanyUploadFormService,
  ChatService,
  Auth0Service,
  UploadFormMeetingTypeService,
  AttendeeRestrictionService,
  SpeakerSelectionService,
  JobInstanceService,
  JobTypeService,
  EventTourService,
  CommonReportService,
  RecommendedForService,
  QuestionTooltipService,
  CountryService,
  CountryStateService,
  VendorTypeService,
  PublicTaskService,
  SpeakerTieringLevelService,
  GrantTypeService,
  GrantApplicantService,
  ClientCodeService,
  PrincipalApplicantProfileService,
  SecondaryApplicantProfileService,
  HolidayService,
  CompanyHolidayService,
  EnvSyncService,
} from './_services';

// Components
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { SharedModule } from 'app/_components/shared.module';
import { ChatbotModule } from './chatbot/chatbot.module';
import { KnowledgeBaseModule } from './knowledge-base/knowledge-base.module';

import { TitlizePipe } from './_pipes/titlize.pipe';

import {
  SiteLayoutComponent,
  SiteHeaderComponent,
  SiteFooterComponent,
  LoginLayoutComponent,
  SandboxBannerComponent,
} from './_layouts';
import { BkContextSwitcherComponent } from './_layouts/site-header/widgets/context-switcher/bk-context-switcher.component';
import { HeaderAccountMenuComponent } from './_layouts/site-header/widgets/account-menu/header-account-menu.component';
import { HeaderPrimaryNavComponent } from './_layouts/site-header/widgets/primary-nav/header-primary-nav.component';
import { DesignSystemModule } from './design-system/design-system.module';

import { USER_IDLE_CONFIG } from './app.constants';
import { LoginComponent } from './login/login.component';
import { ForgotPasswordComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './forgot-password/reset-password.component';
import { NotFoundComponent } from './404/not-found.component';
import { StaticPageComponent } from './static/static-page.component';
import { UserIdleModalComponent } from './user-idle-modal.component';
import { TokenExpiryModalComponent } from './token-expiry-modal.component';
import { RedirectComponent } from './redirect.component';
import { KolQuestionnaireComponent } from './kol-questionnaire/kol-questionnaire.component';
import { DummyComponent } from './dummy.component';
import { LogoutComponent } from './logout.component';
import { CopyToastComponent } from './_components/copy-toast/copy-toast.component';

@NgModule({
  declarations: [
    AppComponent,
    SiteLayoutComponent,
    SiteHeaderComponent,
    BkContextSwitcherComponent,
    HeaderAccountMenuComponent,
    HeaderPrimaryNavComponent,
    SiteFooterComponent,
    SandboxBannerComponent,
    LoginLayoutComponent,
    LoginComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
    UserIdleModalComponent,
    TokenExpiryModalComponent,
    RedirectComponent,
    KolQuestionnaireComponent,
    NotFoundComponent,
    StaticPageComponent,
    DummyComponent,
    LogoutComponent,
    CopyToastComponent,
  ],
  imports: [
    SharedModule,
    DesignSystemModule,
    ChatbotModule,
    KnowledgeBaseModule,
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatNativeDateModule,
    AppRoutingModule,
    LoadingBarRouterModule,
    LoadingBarHttpClientModule,
    UserIdleModule.forRoot(USER_IDLE_CONFIG),
    ToastrModule.forRoot({
      timeOut: 3000,
      positionClass: 'toast-top-center',
      toastComponent: CopyToastComponent,
    }),
    SocketIoModule.forRoot({
      url: environment.socketUrl,
      options: {
        autoConnect: false,
        transports: [ 'websocket' ],
      },
    }),
    SocialLoginModule,
  ],
  entryComponents: [ UserIdleModalComponent, TokenExpiryModalComponent ],
  providers: [
    DatePipe,
    DecimalPipe,
    CurrencyPipe,
    PercentPipe,
    TitlizePipe,

    AuthenticationService,
    {
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthenticationService) => () => auth.ensureServerSessionValid(),
      deps: [AuthenticationService],
      multi: true,
    },
    AuthGuard,
    PendingChangesGuard,
    ProfileService,
    CompanyService,
    SandboxService,
    AgencyService,
    AgencyConfigService,
    BudgetService,
    CompanyBudgetService,
    BureauService,
    BureauBudgetService,
    TopicService,
    EventService,
    DropdownService,
    CompanyDropdownSettingService,
    LanguageService,
    MeetingTypeService,
    QuestionService,
    EvaluationService,
    ChecklistService,
    WorkflowService,
    SpeakerTaskService,
    BureauTaskService,
    RequestFormService,
    RequestFormDefaultValueService,
    PreRequestFormService,
    BureauMeetingTypeService,
    ApprovalGroupService,
    SpeakerApprovalGroupService,
    StaffService,
    ClientService,
    SpeakerService,
    AreaService,
    AreaBudgetService,
    VendorService,
    VendorAgencyService,
    RequestService,
    SettingService,
    FiscalYearService,
    DefaultBudgetService,
    ContactService,
    ContactNoteService,
    FileService,
    GlobalFileService,
    CallPlanService,
    CallPlanContactService,
    AttendeeService,
    HcpService,
    EventStatusService,
    EventStatusRequirementService,
    AreaTypeClientLevelService,
    MeetingTypeClientLevelService,
    EventStatusChangeService,
    EventChangeLogService,
    EventBudgetService,
    EventBudgetStatusChangeService,
    EventRescheduleService,
    EventVenueService,
    EventCatererService,
    EventChatMessageService,
    EventAvCompanyService,
    EventCreditCardService,
    EventTrainingService,
    EventTrainingGroupService,
    CompanyProfileService,
    HolidayService,
    CompanyHolidayService,
    EnvSyncService,
    EquipmentService,
    SpeakerAgreementService,
    SpeakerValidationService,
    SpeakerValidationNoteService,
    SpeakerValidationFileService,
    EventSpeakerService,
    EventPaymentService,
    PaymentService,
    EventNearbyService,
    BudgetTransferService,
    BalanceTransactionService,
    EventCardService,
    DownloadFormService,
    DownloadFormClientLevelService,
    DownloadFormMeetingTypeService,
    EventDownloadFormService,
    ReportService,
    CustomReportService,
    ActionService,
    NotificationService,
    TriggerService,
    UploadFormService,
    CompanyUploadFormService,
    AvEquipmentService,
    VendorAvEquipmentService,
    CompanyAvEquipmentService,
    BureauAvEquipmentService,
    BureauSummaryService,
    EventEquipmentService,
    EventNoteService,
    EventContactService,
    EventNotificationService,
    EventEmailService,
    EventNotificationRuleService,
    EventFileService,
    EventChecklistService,
    EventEvaluationService,
    EventClientAnswerService,
    EventSpeakerAnswerService,
    AttendeeAnswerService,
    AttendeeAnswerAnonymousService,
    CallPlanCustomFieldService,
    ContactCustomFieldService,
    SpeakerCustomFieldService,
    AttendeeCustomFieldService,
    EventCustomFieldService,
    AttendeeRegistrationAnswerService,
    EventCollaborationService,
    ProductService,
    VendorRoomService,
    WorkflowTemplateService,
    DownloadFormGlobalService,
    ContactSubmissionService,
    KolQuestionnaireService,
    EsifFormService,
    SpeakerRateColService,
    SpeakerRateRowService,
    SpeakerRateDefaultService,
    HonorariaRateColService,
    HonorariaRateRowService,
    HonorariaRateDefaultService,
    SpeakerLevelService,
    SpeakerTieringLevelService,
  GrantTypeService,
  GrantApplicantService,
  ClientCodeService,
  PrincipalApplicantProfileService,
  SecondaryApplicantProfileService,
    EmailTemplateService,
    EmailTemplateAlignmentService,
    AgencyEmailTemplateService,
    StaticContentService,
    ContactFieldService,
    SpeakerEsifFormService,
    ForumService,
    StaffChecklistService,
    SupportContactService,
    AlertService,
    SessionSummaryService,
    UsageTrackService,
    UploadFormClientLevelService,
    UploadFormMeetingTypeService,
    AttendeeRestrictionService,
    SpeakerSelectionService,
    ClientLevelService,
    NotificationCategoryService,
    BureauFiscalYearService,
    CompanyBureauAgencyService,
    CompanyBureauTravelAgentService,
    ChartService,
    EventSpeakerPaymentService,
    CoreEventFieldService,
    CoreEventFormService,
    CoreEventService,
    CoreEventAttendeeService,
    CoreEventTravelService,
    CoreEventActivityService,
    CoreEventNoteService,
    CoreInvitationService,
    CoreEventInvitationService,
    CoreEventChatMessageService,
    CoreEventChecklistService,
    CoreEventPaymentService,
    CoreEventBudgetService,
    CoreEventCreditCardService,
    CoreEventCatererService,
    CoreEventInquiryService,
    CoreEventCommunicationService,
    CoreEventAvService,
    CoreEventVenueService,
    CoreEventFileService,
    CoreEventEvaluationService,
    UserService,
    UserNotificationService,
    UserChecklistService,
    PresentationService,
    PresentationCustomService,
    PresentationQrcodeService,
    PresentationAnswerService,
    ContactAddressService,
    ContactLicenseService,
    ContactAssistantService,
    AreaTypeService,
    EventSpeakerTravelService,
    EventSpeakerStayService,
    EventViewRequestService,
    SpeakerAgreementFieldService,
    HourlyRateService,
    UserAlertService,
    EventPresentationService,
    EventClientSignService,
    EventSpeakerSignService,
    EventInquiryService,
    EventWebcastService,
    EventWebcastGroupService,
    InvitationService,
    EventInvitationService,
    UnsubscribeService,
    SocketService,
    UniverseService,
    EventTokenService,
    AttendeeRegistrationFieldsService,
    S3Service,
    HcpValidationSourceService,
    WorkflowBudgetService,
    ClientEventViewService,
    ClientExpenseViewService,
    ClientReportViewService,
    ClientFacultyViewService,
    TrainingModuleService,
    EventSpeakerCarService,
    GlobalEmailTemplateService,
    BlastEmailTemplateService,
    GlobalNotificationService,
    GlobalTriggerService,
    JobInstanceService,
    JobTypeService,
    CommonReportService,
    ChatService,
    Auth0Service,
    EventTourService,
    EventEstimatedTravelService,
    RecommendedForService,
    QuestionTooltipService,
    CountryService,
    CountryStateService,
    VendorTypeService,
    PublicTaskService,
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider(
              environment.GOOGLE_OAUTH2_CLIENT_ID
            ),
          },
          {
            id: FacebookLoginProvider.PROVIDER_ID,
            provider: new FacebookLoginProvider(environment.FACEBOOK_APP_ID),
          },
        ],
        onError: (err) => {
          console.error(err);
        },
      } as SocialAuthServiceConfig,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
  ],
  bootstrap: [ AppComponent ],
})
export class AppModule { }
